import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout, { ModalActionsPortal } from "../ModalBasicLayout";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import Loader from "../../Loaders/Loader";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import {
  BULK_CAMPAIGNS_JOB_KIND,
  startBulkAnalysisJob,
  startBulkGenerationJob,
} from "../../../actions/bulkCampaignsJobs";
import { removeNotification } from "../../../actions/alerts";
import { startCancelCreativeJob } from "../../../actions/jobs";
import { getIsBlockedActions } from "../../../actions/getters";
import { startGetContentItemsByIds } from "../../../actions/user";
import {
  BULK_CAMPAIGNS_ACCOUNTS_UNAVAILABLE,
  BULK_CAMPAIGNS_ANALYZE,
  BULK_CAMPAIGNS_ANALYZING,
  BULK_CAMPAIGNS_CANCEL_ANALYSIS,
  BULK_CAMPAIGNS_DESCRIPTION,
  BULK_CAMPAIGNS_EMPTY_FILE,
  BULK_CAMPAIGNS_CONTENTS_LOAD_ERROR,
  BULK_CAMPAIGNS_EXTRACT_ERROR,
  BULK_CAMPAIGNS_FILE_NOT_SUPPORTED,
  BULK_CAMPAIGNS_FILE_TOO_LARGE,
  BULK_CAMPAIGNS_GENERATE,
  BULK_CAMPAIGNS_GENERATE_ERROR,
  BULK_CAMPAIGNS_GENERATING,
  BULK_CAMPAIGNS_NO_CAMPAIGNS_FOUND,
  BULK_CAMPAIGNS_PLATFORM_NOT_DETECTED,
  BULK_CAMPAIGNS_PLATFORM_NOT_SUPPORTED,
  BULK_CAMPAIGNS_STALE,
  BULK_CAMPAIGNS_TITLE,
  BULK_CAMPAIGNS_TOO_MANY_CONTENTS,
  BULK_CAMPAIGNS_TYPE_NOT_SUPPORTED,
  BULK_CAMPAIGNS_VIEW_CAMPAIGNS,
  BUTTON_CLOSE,
  TASK_ALERT_ESTIMATED_TIME,
} from "../../../i18n/keysTranslations";
import { useCreditActionCosts } from "../../../hooks/useCreditActionCosts";
import { ALERTS_ASYNC_STATUS, ROUTE_CAMPAIGNS } from "../../../utils/constants";
import { getUniqueID } from "../../../utils/numbers";
import {
  BULK_MAX_CONTENTS,
  toBulkContentIDs,
} from "../../../utils/bulkCampaignContent";
import {
  BULK_CAMPAIGNS_ANALYSIS_TASK,
  BULK_CAMPAIGNS_GENERATION_TASK,
} from "../../../utils/taskEstimatedDurations";
import { useCreativeJobProgressLoader } from "../../../hooks/useCreativeJobProgressLoader";
import BulkCampaignsDropzone from "./BulkCampaignsDropzone";
import BulkCampaignsContents from "./BulkCampaignsContents";
import BulkCampaignSummary from "./BulkCampaignSummary";
import BulkCampaignResult from "./BulkCampaignResult";
import { getBulkCampaignTypeConfig } from "./bulkCampaignTypes";
import { formatBulkFileSize, validateBulkFile } from "./bulkCampaignsConstants";
import {
  buildBulkReview,
  measurePoolContentSize,
  mergePoolContents,
  readArray,
  sameContentIDs,
  toPoolContent,
  toPoolContentFromSelection,
  translateBulkToken,
} from "./bulkCampaignModel";

const STATUS = {
  idle: "idle",
  ready: "ready",
  analyzing: "analyzing",
  reviewing: "reviewing",
  generating: "generating",
  done: "done",
  error: "error",
};

const SETUP_STATUSES = new Set([STATUS.idle, STATUS.ready, STATUS.error]);

const ERROR_MESSAGE_KEYS = {
  unsupportedFileType: BULK_CAMPAIGNS_FILE_NOT_SUPPORTED,
  emptyFile: BULK_CAMPAIGNS_EMPTY_FILE,
  fileTooLarge: BULK_CAMPAIGNS_FILE_TOO_LARGE,
  platformNotDetected: BULK_CAMPAIGNS_PLATFORM_NOT_DETECTED,
  platformNotSupported: BULK_CAMPAIGNS_PLATFORM_NOT_SUPPORTED,
  campaignTypeNotSupported: BULK_CAMPAIGNS_TYPE_NOT_SUPPORTED,
  noCampaignsFound: BULK_CAMPAIGNS_NO_CAMPAIGNS_FOUND,
  tooManyContents: BULK_CAMPAIGNS_TOO_MANY_CONTENTS,
  accountsUnavailable: BULK_CAMPAIGNS_ACCOUNTS_UNAVAILABLE,
};

const EMPTY_GENERATION_DATA = {};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const contentIDsDepKey = (contentIDs) =>
  Array.isArray(contentIDs) ? contentIDs.join("|") : "";

/**
 * The analysis / generation run as background jobs (notifications in redux).
 * The modal only renders the job it is attached to, so it can be closed and
 * reopened from the alert at any moment.
 */
const ModalBulkCampaigns = ({
  modalOpen,
  onCloseModal = () => {},
  initialJobID = null,
  embedded = false,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();
  const [jobID, setJobID] = useState(initialJobID);
  const [ignoreJob, setIgnoreJob] = useState(false);
  const [file, setFile] = useState(null);
  const [contents, setContents] = useState([]);
  const [review, setReview] = useState(null);
  const [stale, setStale] = useState(false);
  const [fileError, setFileError] = useState(null);
  const realJobIDRef = useRef(initialJobID);
  const activeLocalJobIDRef = useRef(null);
  const cancelledLocalJobIDsRef = useRef(new Set());
  const seededJobIDRef = useRef(null);

  const job = useSelectorApp((state) =>
    jobID
      ? (state.alerts?.notifications || []).find((item) => item.id === jobID)
      : null
  );

  const generationData = job?.generationData ?? EMPTY_GENERATION_DATA;
  const contentIDsKey = contentIDsDepKey(generationData.contentIDs);
  const contentIDsRef = useRef(generationData.contentIDs);
  contentIDsRef.current = generationData.contentIDs;
  const reviewRef = useRef(null);
  const setReviewState = useCallback((nextReview) => {
    reviewRef.current = nextReview;
    setReview(nextReview);
  }, []);
  const attached = Boolean(job) && !ignoreJob;
  const isGenerationJob =
    attached && generationData.kind === BULK_CAMPAIGNS_JOB_KIND.generation;
  const isJobLoading =
    attached &&
    job.status !== ALERTS_ASYNC_STATUS.SUCCESS &&
    job.status !== ALERTS_ASYNC_STATUS.ERROR;
  const isJobError = attached && job?.status === ALERTS_ASYNC_STATUS.ERROR;
  const isJobSuccess = attached && job?.status === ALERTS_ASYNC_STATUS.SUCCESS;
  const rememberedFileName = generationData.fileName || review?.fileName || "";

  let status = STATUS.idle;
  if (attached && isGenerationJob && isJobLoading) {
    status = STATUS.generating;
  } else if (attached && isGenerationJob && isJobSuccess) {
    status = STATUS.done;
  } else if (attached && !isGenerationJob && isJobLoading) {
    status = STATUS.analyzing;
  } else if (review) {
    status = STATUS.reviewing;
  } else if (attached && isJobError) {
    status = STATUS.error;
  } else if (file || rememberedFileName) {
    status = STATUS.ready;
  }

  const isAnalyzing = status === STATUS.analyzing;
  const isGenerating = status === STATUS.generating;
  const showSetup = SETUP_STATUSES.has(status);
  const campaignCount = readArray(review?.campaigns).length;
  const { formattedRemainingTime } = useCreativeJobProgressLoader({
    storageKey: isAnalyzing || isGenerating ? jobID : null,
    jobType: isGenerating
      ? BULK_CAMPAIGNS_GENERATION_TASK
      : BULK_CAMPAIGNS_ANALYSIS_TASK,
    isTerminal: !isAnalyzing && !isGenerating,
    itemCount: campaignCount,
  });
  const bulkTypeConfig = getBulkCampaignTypeConfig({
    platform: review?.platform || generationData.platform,
    campaignType: review?.campaignType || generationData.campaignType,
  });

  let error = fileError;
  if (!error && isJobError) {
    const code = generationData.errorCode;
    error = {
      key:
        ERROR_MESSAGE_KEYS[code] ||
        (isGenerationJob
          ? BULK_CAMPAIGNS_GENERATE_ERROR
          : BULK_CAMPAIGNS_EXTRACT_ERROR),
      values: {
        platform:
          translateBulkToken({
            t,
            token: generationData.platform,
            prefix: "bulk.platform",
          }) || generationData.platform,
        limit: BULK_MAX_CONTENTS,
      },
    };
  }

  const onJobCreated = (localJobID) => (realJobID) => {
    if (cancelledLocalJobIDsRef.current.has(localJobID)) {
      dispatch(startCancelCreativeJob({ jobID: realJobID }));
      dispatch(removeNotification(realJobID));
      return;
    }
    if (activeLocalJobIDRef.current !== localJobID) return;
    realJobIDRef.current = realJobID;
    setJobID((current) => (current === localJobID ? realJobID : current));
  };

  const detachFromJob = ({ cancelInFlight }) => {
    const localJobID = activeLocalJobIDRef.current;
    const realJobID = realJobIDRef.current;
    if (cancelInFlight) {
      if (localJobID) cancelledLocalJobIDsRef.current.add(localJobID);
      if (localJobID) dispatch(removeNotification(localJobID));
      if (realJobID) {
        dispatch(startCancelCreativeJob({ jobID: realJobID }));
        if (realJobID !== localJobID) {
          dispatch(removeNotification(realJobID));
        }
      }
    }
    activeLocalJobIDRef.current = null;
    realJobIDRef.current = null;
    setIgnoreJob(true);
    setJobID(null);
    setReviewState(null);
    seededJobIDRef.current = null;
  };

  const markSelectionStale = () => {
    const hadReview = Boolean(reviewRef.current);
    detachFromJob({ cancelInFlight: false });
    if (hadReview) setStale(true);
  };

  const onContentsSelected = (items) => {
    const next = readArray(items)
      .map((item) => toPoolContent(item))
      .filter(Boolean);
    if (sameContentIDs({ left: contents, right: next })) return;

    markSelectionStale();
    setContents(next);
    setFileError(null);
  };

  const onGallerySelected = async (items) => {
    try {
      const selection = readArray(items);
      const contentIDs = toBulkContentIDs(selection);
      const response = await dispatch(
        startGetContentItemsByIds({ contentIDs })
      );
      const records = new Map(
        readArray(response?.items).map((item) => [
          String(item?.id || item?.contentID),
          item,
        ])
      );
      const hydrated = (
        await Promise.all(
          selection
            .filter((item) => records.has(String(item?.id || item?.contentID)))
            .map((item) =>
              measurePoolContentSize(
                toPoolContentFromSelection({
                  selection: item,
                  record: records.get(String(item?.id || item?.contentID)),
                })
              )
            )
        )
      ).filter(Boolean);
      const merged = mergePoolContents({
        current: contents,
        incoming: hydrated,
        append: true,
      });
      const exceeded = merged.length > BULK_MAX_CONTENTS;
      const next = merged.slice(0, BULK_MAX_CONTENTS);
      const idsChanged = !sameContentIDs({ left: contents, right: next });
      if (idsChanged) markSelectionStale();
      setContents(next);
      setFileError(
        exceeded
          ? {
              key: BULK_CAMPAIGNS_TOO_MANY_CONTENTS,
              values: { limit: BULK_MAX_CONTENTS },
            }
          : null
      );
    } catch (_error) {
      setFileError({ key: BULK_CAMPAIGNS_CONTENTS_LOAD_ERROR });
    }
  };

  const onFileSelected = (nextFile) => {
    const code = validateBulkFile(nextFile);
    if (code) {
      setFileError({ key: ERROR_MESSAGE_KEYS[code] });
      return;
    }

    const hadReview = Boolean(reviewRef.current);
    detachFromJob({ cancelInFlight: false });
    if (hadReview) setStale(true);
    setFile(nextFile);
    setFileError(null);
  };

  const onAnalyze = async () => {
    const code = validateBulkFile(file);
    if (code) {
      setFileError({ key: ERROR_MESSAGE_KEYS[code] });
      return;
    }
    if (contents.length > BULK_MAX_CONTENTS) {
      setFileError({
        key: BULK_CAMPAIGNS_TOO_MANY_CONTENTS,
        values: { limit: BULK_MAX_CONTENTS },
      });
      return;
    }

    setFileError(null);
    try {
      const base64Data = await readFileAsBase64(file);
      const contentIDs = toBulkContentIDs(contents);
      const newJobID = getUniqueID();
      activeLocalJobIDRef.current = newJobID;
      realJobIDRef.current = null;
      seededJobIDRef.current = null;
      setIgnoreJob(false);
      setStale(false);
      setReviewState(null);
      dispatch(
        startBulkAnalysisJob({
          jobID: newJobID,
          fileName: file.name,
          base64Data,
          contentIDs,
          onJobCreated: onJobCreated(newJobID),
        })
      );
      setJobID(newJobID);
    } catch (_error) {
      setFileError({ key: BULK_CAMPAIGNS_EXTRACT_ERROR });
    }
  };

  const onCancelAnalysis = () => {
    detachFromJob({ cancelInFlight: true });
  };

  const onGenerate = async () => {
    if (!review || campaignCount === 0) return;
    setFileError(null);

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
        campaignUsageContext: {
          platform: bulkTypeConfig.usagePlatform,
          generatedPlatforms: {},
          newCampaignsCount: campaignCount,
        },
      })
    );
    if (isBlocked) return;

    const campaigns = review.campaigns;
    const newJobID = getUniqueID();
    activeLocalJobIDRef.current = newJobID;
    realJobIDRef.current = null;
    setIgnoreJob(false);
    dispatch(
      startBulkGenerationJob({
        jobID: newJobID,
        platform: review.platform,
        campaignType: review.campaignType,
        campaigns,
        fileName: review.fileName || file?.name || "",
        contentIDs: toBulkContentIDs(contents),
        onJobCreated: onJobCreated(newJobID),
      })
    );
    setJobID(newJobID);
  };

  const onViewCampaigns = () => {
    navigate(ROUTE_CAMPAIGNS);
    onCloseModal();
  };

  const onClose = () => {
    setJobID(null);
    onCloseModal();
  };

  useEffect(() => {
    if (!modalOpen) return;
    setJobID(initialJobID ?? null);
    setIgnoreJob(false);
    setFile(null);
    setFileError(null);
    setReviewState(null);
    setStale(false);
    setContents([]);
    seededJobIDRef.current = null;
    activeLocalJobIDRef.current = null;
    realJobIDRef.current = initialJobID ?? null;
  }, [modalOpen, initialJobID, setReviewState]);

  useEffect(() => {
    if (!modalOpen || ignoreJob || !attached || !jobID) return;
    if (reviewRef.current) return;
    if (seededJobIDRef.current === jobID) return;

    const canSeedAnalysis = !isGenerationJob && isJobSuccess;
    const canSeedGenerationError =
      isGenerationJob && isJobError && !isJobSuccess;
    if (!canSeedAnalysis && !canSeedGenerationError) return;

    if (readArray(generationData.campaigns).length === 0) return;

    const nextReview = buildBulkReview(generationData);
    seededJobIDRef.current = jobID;
    setReviewState(nextReview);
    setStale(false);
  }, [
    modalOpen,
    ignoreJob,
    attached,
    jobID,
    isGenerationJob,
    isJobSuccess,
    isJobError,
    generationData,
    setReviewState,
  ]);

  useEffect(() => {
    if (!modalOpen || !jobID) return undefined;
    const ids = contentIDsRef.current;
    if (!Array.isArray(ids) || ids.length === 0) return undefined;

    let cancelled = false;
    const restore = async () => {
      const response = await dispatch(
        startGetContentItemsByIds({ contentIDs: ids })
      );
      if (cancelled) return;
      const restored = (
        await Promise.all(
          readArray(response?.items)
            .map((item) => toPoolContent(item))
            .filter(Boolean)
            .slice(0, BULK_MAX_CONTENTS)
            .map((item) => measurePoolContentSize(item))
        )
      ).filter(Boolean);
      if (cancelled) return;
      setContents((current) =>
        mergePoolContents({ current, incoming: restored })
      );
    };
    restore();

    return () => {
      cancelled = true;
    };
  }, [modalOpen, jobID, contentIDsKey, dispatch]);

  const displayFileName =
    file?.name || (status === STATUS.idle ? "" : rememberedFileName);
  const canAnalyze =
    Boolean(file) && (status === STATUS.ready || status === STATUS.error);
  const canGenerate =
    status === STATUS.reviewing && campaignCount > 0 && !stale;

  let primaryButton = null;
  if (canAnalyze) {
    primaryButton = { text: BULK_CAMPAIGNS_ANALYZE, onClick: onAnalyze };
  } else if (canGenerate) {
    primaryButton = { text: BULK_CAMPAIGNS_GENERATE, onClick: onGenerate };
  } else if (status === STATUS.done) {
    primaryButton = {
      text: BULK_CAMPAIGNS_VIEW_CAMPAIGNS,
      onClick: onViewCampaigns,
    };
  }

  const body = (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
    >
      {showSetup && (
        <>
          <BulkCampaignsDropzone
            fileName={displayFileName}
            fileSizeLabel={file ? formatBulkFileSize(file.size) : ""}
            onFileSelected={onFileSelected}
          />
          <BulkCampaignsContents
            contents={contents}
            onContentsSelected={onContentsSelected}
            onGallerySelected={onGallerySelected}
          />
        </>
      )}

      {stale && showSetup && (
        <Typography variant="body2" color="warning.main">
          {t(BULK_CAMPAIGNS_STALE)}
        </Typography>
      )}

      {(isAnalyzing || isGenerating) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            py: 4,
          }}
        >
          <Loader size={28} />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {isAnalyzing
              ? t(BULK_CAMPAIGNS_ANALYZING, {
                  fileName: displayFileName,
                  interpolation: { escapeValue: false },
                })
              : t(BULK_CAMPAIGNS_GENERATING)}
          </Typography>
          {isAnalyzing ? (
            <Typography variant="caption" color="text.secondary">
              {t(TASK_ALERT_ESTIMATED_TIME, {
                estimatedTime: formattedRemainingTime,
              })}
            </Typography>
          ) : null}
        </Box>
      )}

      {status === STATUS.reviewing && review && (
        <BulkCampaignSummary review={review} />
      )}

      {status === STATUS.done && <BulkCampaignResult result={generationData} />}

      {error && (
        <Typography variant="body2" color="error">
          {t(error.key, error.values)}
        </Typography>
      )}
    </Box>
  );

  const actions = (
    <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 2, pt: 2 }}>
      <ContainerModalActions
        hasPadding={false}
        propsSecondaryButton={{
          showButton: !embedded,
          text: BUTTON_CLOSE,
          onClick: onClose,
        }}
        propsTertiaryButton={{
          showButton: isAnalyzing,
          text: BULK_CAMPAIGNS_CANCEL_ANALYSIS,
          onClick: onCancelAnalysis,
        }}
        propsPrimaryButton={{
          showButton: Boolean(primaryButton),
          text: primaryButton?.text,
          onClick: primaryButton?.onClick,
        }}
      />
    </DialogActions>
  );

  if (embedded) {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            align="left"
            sx={{ fontWeight: 600, color: "background.paperContrastText" }}
          >
            {t(BULK_CAMPAIGNS_TITLE)}
          </Typography>
          <Typography
            variant="body1"
            align="left"
            sx={{ color: "background.paperContrastText" }}
          >
            {t(BULK_CAMPAIGNS_DESCRIPTION)}
          </Typography>
        </Box>
        {body}
        <ModalActionsPortal>{actions}</ModalActionsPortal>
      </>
    );
  }

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onClose}
      fullWidth={true}
      scroll="paper"
      maxWidth="sm"
      title={t(BULK_CAMPAIGNS_TITLE)}
      description={t(BULK_CAMPAIGNS_DESCRIPTION)}
      sxTitle={{ color: "background.paperContrastText" }}
      sxDescription={{ color: "background.paperContrastText" }}
    >
      {body}
      {actions}
    </ModalBasicLayout>
  );
};

export default ModalBulkCampaigns;
